import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Card from "../../components/common/Card";
import Button from "../../components/common/Button";
import Select from "../../components/common/Select";
import Alert from "../../components/common/Alert";
import Modal from "../../components/common/Modal";
import Table from "../../components/common/Table";
import Toast from "../../components/common/Toast";
import PageHeader from "../../components/common/PageHeader";
import {
  useGetPrintersQuery,
  useDeletePrinterMutation,
  useGetStationsQuery,
  useUpdateStationMutation,
  useGetKiosksQuery,
  useUpdateKioskMutation,
} from "../../store/api";
import { getStationErrorMessage } from "../../utils/stationErrorHandler";

const getApiErrorCode = (err) =>
  err?.data?.code || err?.data?.errorCode || err?.data?.title || null;

const getPrinterErrorMessage = (err, fallback) => {
  const code = getApiErrorCode(err);
  const errors = {
    PRINTER_NAME_EXISTS: "Printer name already exists.",
    PRINTER_NAME_REQUIRED: "Printer name is required.",
    PRINTER_TYPE_REQUIRED: "Printer type is required.",
    PRINTER_TYPE_INVALID: "Printer type is invalid.",
    PRINTER_IP_REQUIRED: "IP address is required for LAN printers.",
    PRINTER_PORT_REQUIRED: "Port is required for LAN printers.",
    PRINTER_DRIVER_REQUIRED: "Driver name is required for Windows/USB printers.",
    PRINTER_NOT_FOUND: "Selected printer is no longer available.",
  };
  return (
    errors[code] ||
    err?.data?.detail ||
    err?.data?.message ||
    fallback ||
    "Something went wrong. Please try again."
  );
};

const buildStationPayload = (station, printerId) => ({
  stationCode: (station.stationCode || "").trim().toUpperCase(),
  displayName: (station.displayName || "").trim(),
  sort: Number(station.sort) || 0,
  printPrefix: (station.printPrefix || "").trim().toUpperCase(),
  printerId: printerId || null,
  isActive: station.isActive !== undefined ? station.isActive : true,
  isStandaloneDisplay: Boolean(
    station.isStandaloneDisplay ?? station.IsStandaloneDisplay,
  ),
  assignedKdsDeviceIds: Array.isArray(station.assignedKdsDeviceIds)
    ? station.assignedKdsDeviceIds
    : Array.isArray(station.AssignedKdsDeviceIds)
      ? station.AssignedKdsDeviceIds
      : [station.kdsDeviceId || station.KdsDeviceId].filter(Boolean),
  isKitchenDisplayActive:
    station.isKitchenDisplayActive !== undefined
      ? station.isKitchenDisplayActive
      : true,
  isOrderDisplayActive:
    station.isOrderDisplayActive !== undefined
      ? station.isOrderDisplayActive
      : true,
});

const buildKioskPayload = (kiosk, printerDeviceId) => {
  const storedDeviceName =
    typeof window !== "undefined"
      ? localStorage.getItem("edgeDeviceName")
      : null;
  const storedDeviceToken =
    typeof window !== "undefined"
      ? localStorage.getItem("edgeDeviceToken")
      : null;

  return {
    deviceName: storedDeviceName || kiosk?.deviceName || "",
    deviceKey: storedDeviceToken || kiosk?.deviceKey || "",
  pairingCode: kiosk?.pairingCode || "",
  bridgeBaseUrl: kiosk?.bridgeBaseUrl || null,
  signalrUrl: kiosk?.signalrUrl || null,
  inactivitySeconds: Number(kiosk?.inactivitySeconds) || 60,
  paymentsEnabled:
    kiosk?.paymentsEnabled !== undefined ? kiosk.paymentsEnabled : true,
  printingEnabled:
    kiosk?.printingEnabled !== undefined ? kiosk.printingEnabled : true,
  isActive: kiosk?.isActive !== undefined ? kiosk.isActive : true,
  printerDeviceId: printerDeviceId || null,
  };
};

const PrintersSettings = () => {
  const navigate = useNavigate();
  const {
    data: printersData,
    isLoading: printersLoading,
    isError: printersError,
    error: printersErrorDetail,
    refetch: refetchPrinters,
  } = useGetPrintersQuery();
  const {
    data: stationsData,
    isLoading: stationsLoading,
    isError: stationsError,
    error: stationsErrorDetail,
    refetch: refetchStations,
  } = useGetStationsQuery();
  const {
    data: kiosksData,
    isLoading: kiosksLoading,
    isError: kiosksError,
    error: kiosksErrorDetail,
  } = useGetKiosksQuery();

  const [deletePrinter] = useDeletePrinterMutation();
  const [updateStation] = useUpdateStationMutation();
  const [updateKiosk, updateKioskState] = useUpdateKioskMutation();

  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    printerId: null,
    printerName: "",
  });
  const [stationSavingId, setStationSavingId] = useState(null);
  const [stationError, setStationError] = useState("");
  const [receiptPrinterId, setReceiptPrinterId] = useState("");
  const [receiptError, setReceiptError] = useState("");
  const [toast, setToast] = useState(null);

  const printers = Array.isArray(printersData) ? printersData : [];
  const stations = Array.isArray(stationsData) ? stationsData : [];
  const kiosks = Array.isArray(kiosksData) ? kiosksData : [];

  const activePrinters = useMemo(
    () => printers.filter((printer) => printer.isActive !== false),
    [printers],
  );

  const sortedStations = useMemo(
    () => [...stations].sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0)),
    [stations],
  );

  const stationPrinterIds = useMemo(() => {
    const ids = new Set();
    stations.forEach((station) => {
      if (station.printerId) ids.add(station.printerId);
    });
    return ids;
  }, [stations]);

  const kioskPrinterIds = useMemo(() => {
    const ids = new Set();
    kiosks.forEach((kiosk) => {
      if (kiosk.printerDeviceId) ids.add(kiosk.printerDeviceId);
    });
    return ids;
  }, [kiosks]);

  const preferredDeviceId =
    typeof window !== "undefined"
      ? localStorage.getItem("edgeDeviceId")
      : null;
  const preferredDeviceToken =
    typeof window !== "undefined"
      ? localStorage.getItem("edgeDeviceToken")
      : null;

  const preferredKiosk = useMemo(() => {
    if (!preferredDeviceId && !preferredDeviceToken) return null;
    return (
      kiosks.find(
        (k) =>
          k.kioskId === preferredDeviceId ||
          k.deviceId === preferredDeviceId ||
          k.deviceKey === preferredDeviceToken,
      ) || null
    );
  }, [kiosks, preferredDeviceId, preferredDeviceToken]);

  const fallbackKiosk = useMemo(() => {
    if (kiosks.length === 0) return null;
    const active = kiosks.find((k) => k.isActive !== false);
    return active || kiosks[0];
  }, [kiosks]);

  const activeKiosk = preferredKiosk || fallbackKiosk;
  const isUsingFallbackKiosk = !preferredKiosk && Boolean(activeKiosk);

  useEffect(() => {
    setReceiptPrinterId(activeKiosk?.printerDeviceId || "");
    setReceiptError("");
  }, [activeKiosk?.kioskId, activeKiosk?.printerDeviceId]);

  const receiptSelectionDirty =
    (activeKiosk?.printerDeviceId || "") !== receiptPrinterId;

  const handleEdit = (printer) => {
    navigate(`/settings/printers/${printer.printerId}/edit`);
  };

  const handleDelete = (printer) => {
    setDeleteModal({
      isOpen: true,
      printerId: printer.printerId,
      printerName: printer.name || "this printer",
    });
  };

  const confirmDelete = async () => {
    try {
      await deletePrinter(deleteModal.printerId).unwrap();
      setDeleteModal({ isOpen: false, printerId: null, printerName: "" });
      setToast({
        id: Date.now(),
        tone: "success",
        message: "Printer deleted successfully.",
      });
      refetchPrinters();
    } catch (err) {
      setDeleteModal({ isOpen: false, printerId: null, printerName: "" });
      setToast({
        id: Date.now(),
        tone: "error",
        message: getPrinterErrorMessage(err, "Failed to delete printer."),
      });
    }
  };

  const handleStationPrinterChange = async (station, printerId) => {
    if ((station.printerId || "") === (printerId || "")) {
      return;
    }
    setStationError("");
    setStationSavingId(station.stationId);
    try {
      await updateStation({
        stationId: station.stationId,
        payload: buildStationPayload(station, printerId),
      }).unwrap();
      refetchStations();
      setToast({
        id: Date.now(),
        tone: "success",
        message: "Station printer updated.",
      });
    } catch (err) {
      setStationError(
        getStationErrorMessage(err, "Failed to update station printer."),
      );
    } finally {
      setStationSavingId(null);
    }
  };

  const handleReceiptSave = async () => {
    if (!activeKiosk) return;
    setReceiptError("");
    try {
      await updateKiosk({
        kioskId: activeKiosk.kioskId,
        payload: buildKioskPayload(activeKiosk, receiptPrinterId),
      }).unwrap();
      setToast({
        id: Date.now(),
        tone: "success",
        message: "Default receipt printer updated.",
      });
    } catch (err) {
      setReceiptError(
        err?.data?.detail ||
          err?.data?.message ||
          "Failed to update receipt printer.",
      );
    }
  };

  const printerColumns = [
    { key: "name", label: "Name" },
    { key: "type", label: "Type" },
    { key: "connection", label: "IP / Port" },
    { key: "driver", label: "Driver Name" },
    { key: "status", label: "Status" },
    { key: "actions", label: "Actions" },
  ];

  const printerRows = printers.map((printer) => {
    const inUse =
      stationPrinterIds.has(printer.printerId) ||
      kioskPrinterIds.has(printer.printerId);
    const deleteDisabled = inUse;

    return {
      id: printer.printerId,
      name: printer.name || "Unnamed",
      type: printer.printerType || "Unknown",
      connection:
        printer.printerType === "LAN"
          ? `${printer.ipAddress || "N/A"}:${printer.port || "N/A"}`
          : "N/A",
      driver:
        printer.printerType === "WINDOWS" || printer.printerType === "USB"
          ? printer.driverName || "N/A"
          : "N/A",
      status: printer.isActive !== false ? "Active" : "Inactive",
      actions: (
        <div className="flex gap-2">
          <button
            type="button"
            className="p-1 text-[#4f46e5] hover:bg-indigo-50 dark:hover:bg-indigo-900/30 rounded transition"
            onClick={() => handleEdit(printer)}
            title="Edit"
            aria-label="Edit printer"
          >
            <svg
              className="h-5 w-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
              />
            </svg>
          </button>
          <span
            title={
              deleteDisabled
                ? "Printer is in use (assigned to a station or receipt printer)"
                : "Delete"
            }
          >
            <button
              type="button"
              className={`p-1 rounded transition ${
                deleteDisabled
                  ? "text-slate-300 dark:text-slate-600 cursor-not-allowed"
                  : "text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-900/30"
              }`}
              onClick={() => {
                if (!deleteDisabled) handleDelete(printer);
              }}
              aria-label="Delete printer"
              disabled={deleteDisabled}
            >
              <svg
                className="h-5 w-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                />
              </svg>
            </button>
          </span>
        </div>
      ),
    };
  });

  const stationColumns = [
    { key: "name", label: "Station Name" },
    { key: "code", label: "Code" },
    { key: "printer", label: "Printer" },
  ];

  const stationRows = sortedStations.map((station) => {
    return {
      id: station.stationId,
      name: station.displayName || station.stationCode || "Station",
      code: station.stationCode || "N/A",
      printer: (
        <Select
          value={station.printerId || ""}
          onChange={(e) =>
            handleStationPrinterChange(
              station,
              e.target.value ? e.target.value : null,
            )
          }
          disabled={stationSavingId === station.stationId}
        >
          <option value="">No printer</option>
          {activePrinters.map((printer) => (
            <option key={printer.printerId} value={printer.printerId}>
              {printer.name}
            </option>
          ))}
        </Select>
      ),
    };
  });

  return (
    <div className="space-y-6">
      {toast && (
        <Toast
          message={toast.message}
          tone={toast.tone}
          onClose={() => setToast(null)}
        />
      )}

      <PageHeader
        title="Printers"
        subtitle="Manage printer registry, station mapping, and receipt printer."
        actions={
          <Button size="sm" onClick={() => navigate("/settings/printers/new")}>
            Add Printer
          </Button>
        }
      />

      <Card className="space-y-8">
        <section className="space-y-4">
          <div>
            <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">
              Printer Registry
            </h2>
            <p className="text-sm text-slate-600 dark:text-slate-400">
              Register printers for kitchen and receipt printing.
            </p>
          </div>

          {printersLoading && (
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Loading printers...
            </p>
          )}
          {printersError && (
            <Alert tone="error">
              {getPrinterErrorMessage(
                printersErrorDetail,
                "Failed to load printers.",
              )}
            </Alert>
          )}
          {!printersLoading && !printersError && (
            <Table
              columns={printerColumns}
              rows={printerRows}
              emptyText="No printers registered."
            />
          )}
        </section>

        <section className="space-y-4">
          <div>
            <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">
              Station Mapping
            </h2>
            <p className="text-sm text-slate-600 dark:text-slate-400">
              Assign printers to kitchen stations. Kitchen tokens will fail if no
              station printer is set.
            </p>
          </div>

          {stationsLoading && (
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Loading stations...
            </p>
          )}
          {stationsError && (
            <Alert tone="error">
              {getStationErrorMessage(
                stationsErrorDetail,
                "Failed to load stations.",
              )}
            </Alert>
          )}
          {stationError && <Alert tone="error">{stationError}</Alert>}
          {!stationsLoading && !stationsError && (
            <Table
              columns={stationColumns}
              rows={stationRows}
              emptyText="No stations available."
            />
          )}
        </section>

        <section className="space-y-4">
          <div>
            <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">
              Default Receipt Printer
            </h2>
            <p className="text-sm text-slate-600 dark:text-slate-400">
              This is device-scoped and applies to the currently selected POS/Kiosk.
            </p>
          </div>

          {kiosksLoading && (
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Loading device settings...
            </p>
          )}
          {kiosksError && (
            <Alert tone="error">
              {kiosksErrorDetail?.data?.detail ||
                kiosksErrorDetail?.data?.message ||
                "Failed to load kiosk settings."}
            </Alert>
          )}

          {!kiosksLoading && !kiosksError && (
            <div className="space-y-3">
              {isUsingFallbackKiosk && activeKiosk && (
                <Alert tone="warning">
                  No device selected; applying to {activeKiosk.deviceName || "this kiosk"}.
                </Alert>
              )}
              {!activeKiosk && (
                <Alert tone="warning">
                  No kiosks found. Create a kiosk to set a receipt printer.
                </Alert>
              )}
              {activeKiosk && (
                <>
                  <div className="max-w-md">
                    <Select
                      label="Receipt Printer"
                      value={receiptPrinterId}
                      onChange={(e) => setReceiptPrinterId(e.target.value)}
                    >
                      <option value="">Select printer</option>
                      {activePrinters.map((printer) => (
                        <option key={printer.printerId} value={printer.printerId}>
                          {printer.name}
                        </option>
                      ))}
                    </Select>
                  </div>
                  {receiptError && <Alert tone="error">{receiptError}</Alert>}
                  <Button
                    size="sm"
                    onClick={handleReceiptSave}
                    disabled={!receiptSelectionDirty || updateKioskState.isLoading}
                  >
                    {updateKioskState.isLoading ? "Saving..." : "Save Receipt Printer"}
                  </Button>
                </>
              )}
            </div>
          )}
        </section>
      </Card>

      <Modal
        isOpen={deleteModal.isOpen}
        onClose={() =>
          setDeleteModal({ isOpen: false, printerId: null, printerName: "" })
        }
        title="Delete Printer"
        confirmText="Delete"
        confirmVariant="primary"
        onConfirm={confirmDelete}
      >
        <p>
          Are you sure you want to delete{" "}
          <strong>{deleteModal.printerName}</strong>?
        </p>
        <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">
          This action cannot be undone.
        </p>
      </Modal>
    </div>
  );
};

export default PrintersSettings;
